package ai.wagemix.web.components

import ai.wagemix.web.theme.WMControlCornerVar
import ai.wagemix.web.theme.WMControlHeightVar
import ai.wagemix.web.theme.WMControlSmHeightVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMMeterHeightVar
import ai.wagemix.web.theme.WMOpacity
import ai.wagemix.web.theme.WMPillCornerVar
import ai.wagemix.web.theme.WMSegmentPaddingVar
import ai.wagemix.web.theme.WMSliderThumbVar
import ai.wagemix.web.theme.WMTabularTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.FSMotionShortVar
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.forms.Checkbox
import com.varabyte.kobweb.silk.components.forms.CheckboxSize
import com.varabyte.kobweb.silk.components.forms.Switch
import com.varabyte.kobweb.silk.components.forms.SwitchSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.max
import org.jetbrains.compose.web.attributes.min
import org.jetbrains.compose.web.attributes.step
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

val WMSegmentedControlStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .padding(WMSegmentPaddingVar.value())
        .gap(WMSegmentPaddingVar.value())
        .borderRadius(WMControlCornerVar.value())
        .backgroundColor(colors.surfaceContainer)
        .height(WMControlHeightVar.value())
}

val WMSegmentStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .setVariable(ButtonVars.Height, WMControlSmHeightVar.value())
            .setVariable(ButtonVars.Color, colors.onSurfaceVariant)
            .setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
            .setVariable(ButtonVars.BackgroundHoverColor, colors.surfaceContainerHigh)
            .setVariable(ButtonVars.BackgroundPressedColor, colors.surfaceContainerHighest)
            .height(WMControlSmHeightVar.value())
            .borderRadius(WMControlCornerVar.value())
            .border(0.px)
            // Each segment is as wide as its label and never narrower; spare width is shared out. With flex: 1 the
            // segments split the width equally and a long label ("From job descriptions") overflowed its segment.
            .flexGrow(1)
            .flexShrink(0)
            .whiteSpace(WhiteSpace.NoWrap)
            .transition(Transition.of("background-color", FSMotionShortVar.value()))
    }
}

/** The selected segment: surface fill, `onSurface` text, a hairline of the control. */
val WMSegmentSelectedStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .setVariable(ButtonVars.Color, colors.onSurface)
        .setVariable(ButtonVars.BackgroundDefaultColor, colors.surface)
        .setVariable(ButtonVars.BackgroundHoverColor, colors.surface)
        .setVariable(ButtonVars.BackgroundPressedColor, colors.surface)
        .border(WMHairlineVar.value(), LineStyle.Solid, colors.outlineVariant)
}

/**
 * Penpot `Wagemix / Segmented Control` (Annual | Hourly, Area | Industry, Upload file |
 * Paste URL, All | From job descriptions). A `tablist` of buttons; [selectedIndex] is the
 * active one.
 */
@Composable
fun WMSegmentedControl(
    options: List<String>,
    selectedIndex: Int,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
    accessibilityString: String? = null,
    enabled: Boolean = true
) {
    Row(
        modifier = WMSegmentedControlStyle.toModifier()
            .role("tablist")
            .then(if (accessibilityString != null) Modifier.ariaLabel(accessibilityString) else Modifier)
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        options.forEachIndexed { index, option ->
            val selected = index == selectedIndex
            Button(
                onClick = { onSelect(index) },
                modifier = WMSegmentStyle.toModifier()
                    .then(if (selected) WMSegmentSelectedStyle.toModifier() else Modifier)
                    .then(if (selected) WMLabelStrongTextStyle.toModifier() else WMLabelTextStyle.toModifier())
                    .role("tab")
                    .attrsModifier { attr("aria-selected", selected.toString()) },
                enabled = enabled
            ) {
                SpanText(option)
            }
        }
    }
}

/**
 * Penpot `Wagemix / Stepper`: −/+ icon buttons around a value label ("2 yrs"). [decrementLabel]
 * and [incrementLabel] name the buttons for assistive technology.
 */
@Composable
fun WMStepper(
    valueText: String,
    onDecrement: () -> Unit,
    onIncrement: () -> Unit,
    decrementLabel: String,
    incrementLabel: String,
    modifier: Modifier = Modifier,
    canDecrement: Boolean = true,
    canIncrement: Boolean = true
) {
    Row(modifier = Modifier.gap(WMTightGapVar.value()).then(modifier), verticalAlignment = Alignment.CenterVertically) {
        WMIconButton(WMIconGlyph.Minus, decrementLabel, kind = WMButtonKind.Secondary, size = WMButtonSize.Sm, enabled = canDecrement, onClick = onDecrement)
        SpanText(
            valueText,
            WMLabelStrongTextStyle.toModifier().then(WMTabularTextStyle.toModifier()).minWidth(48.px).textAlign(TextAlign.Center)
        )
        WMIconButton(WMIconGlyph.Plus, incrementLabel, kind = WMButtonKind.Secondary, size = WMButtonSize.Sm, enabled = canIncrement, onClick = onIncrement)
    }
}

/** Penpot `Wagemix / Checkbox`: Silk's checkbox with the Wagemix colours. */
@Composable
fun WMCheckbox(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    label: String? = null
) {
    val colors = ColorMode.current.toPalette().fs
    Checkbox(
        checked = checked,
        onCheckedChange = onCheckedChange,
        modifier = WMLabelTextStyle.toModifier().then(modifier),
        enabled = enabled,
        size = CheckboxSize.SM,
        borderColor = colors.outline,
        iconColor = colors.onPrimary,
        focusOutlineColor = colors.primary,
        content = label?.let { { SpanText(it) } }
    )
}

/** Penpot `Wagemix / Toggle`: Silk's switch at the small Wagemix size. */
@Composable
fun WMToggle(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    accessibilityString: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val colors = ColorMode.current.toPalette().fs
    Switch(
        checked = checked,
        onCheckedChange = onCheckedChange,
        modifier = Modifier.ariaLabel(accessibilityString).then(modifier),
        enabled = enabled,
        size = SwitchSize.SM,
        focusBorderColor = colors.primary
    )
}

val WMSliderStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .height(WMSliderThumbVar.value())
            .cursor(Cursor.Pointer)
            .styleModifier { property("accent-color", colors.primary.toString()) }
    }
    cssRule(":disabled") { Modifier.opacity(WMOpacity.DISABLED).cursor(Cursor.NotAllowed) }
}

/**
 * Penpot `Wagemix / Slider`: a native range input (keyboard and screen-reader support for
 * free) coloured with the brand through `accent-color`. [value] is [min]..[max].
 * [onValueChange] follows every input while the thumb moves; [onValueCommit] gets the value once
 * when it is released (the native `change` event, also after a keyboard step).
 */
@Composable
fun WMSlider(
    value: Double,
    onValueChange: (Double) -> Unit,
    accessibilityString: String,
    modifier: Modifier = Modifier,
    min: Double = 0.0,
    max: Double = 100.0,
    step: Double = 1.0,
    enabled: Boolean = true,
    onValueCommit: ((Double) -> Unit)? = null
) {
    Input(
        type = InputType.Range,
        attrs = WMSliderStyle.toModifier().then(modifier).toAttrs {
            attr("aria-label", accessibilityString)
            min(min.toString())
            max(max.toString())
            step(step)
            value(value.toString())
            if (!enabled) disabled()
            onInput { event -> event.value?.let { onValueChange(it.toDouble()) } }
            onValueCommit?.let { commit -> onChange { event -> event.value?.let { commit(it.toDouble()) } } }
        }
    )
}

/** A thin progress meter (the sidebar quota meter). [fraction] is 0..1. */
@Composable
fun WMMeter(
    fraction: Double,
    accessibilityString: String,
    modifier: Modifier = Modifier,
    warning: Boolean = false
) {
    val colors = ColorMode.current.toPalette().fs
    val clamped = fraction.coerceIn(0.0, 1.0)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(WMMeterHeightVar.value())
            .borderRadius(WMPillCornerVar.value())
            .backgroundColor(colors.surfaceContainerHigh)
            .role("progressbar")
            .ariaLabel(accessibilityString)
            .attrsModifier {
                attr("aria-valuemin", "0")
                attr("aria-valuemax", "100")
                attr("aria-valuenow", (clamped * 100).toInt().toString())
            }
            .then(modifier)
    ) {
        Row(
            modifier = Modifier
                .width((clamped * 100).percent)
                .height(100.percent)
                .borderRadius(WMPillCornerVar.value())
                .backgroundColor(if (warning) colors.tertiary else colors.primary)
                .transition(Transition.of("width", FSMotionShortVar.value()))
        ) {}
    }
}
